/**
 * Custom Invite — shared wire schemas (plan 2026-09-23-custom-invite-stopgap-plan.md, U1).
 *
 * Single source of truth for:
 *  - `GET /api/flow/enroll/lookup` response (U3)
 *  - `GET /api/flow/enroll/invite-status` response (U3)
 *  - `POST /api/flow/enroll` response (already live; `enroll-service.ts`'s
 *    hand-written `EnrollResultRow` interface is replaced by
 *    `z.infer<typeof EnrollResultRowSchema>` in U2 — see
 *    `terra/no-parallel-schema-and-type`).
 *
 * Both the Express routes (`.parse()` outgoing bodies) and the frontend panel
 * (`validateResponse()` incoming bodies) import from here (K4).
 */

import { z } from 'zod';
import { SenderOptionSchema } from './enrollment';

export const CustomInviteLookupContactSchema = z
  .object({
    contact_id: z.string(),
    first_name: z.string().nullable(),
    last_name: z.string().nullable(),
    /** first + last joined, or null when both empty — the panel's name autofill. */
    full_name: z.string().nullable(),
    title: z.string().nullable(),
    account_id: z.string().nullable(),
    account_name: z.string().nullable(),
    /** Account-level owner/ClayDR emails (Item K) — resolved the same join
     *  shape `loadContact` (enroll-service.ts) uses at send time, so the
     *  panel can show what "Send on behalf of Owner/ClayDR" will resolve to
     *  as soon as a contact is found, not just after a failed send. */
    owner_email: z.string().nullable(),
    claydr_email: z.string().nullable(),
  })
  .strict();

export const CustomInviteCampaignHistoryRowSchema = z
  .object({
    campaign_id: z.string(),
    campaign_name: z.string().nullable(),
    /** ISO timestamp of the most recent membership/invite row for this (email, campaign). */
    last_activity_at: z.string().nullable(),
    /** Salesforce CampaignMember status of the newest member row for this (email, campaign); null when the
     *  only history is a Terra-originated invite with no SFDC membership yet (Item E). */
    cm_status: z.string().nullable(),
    /** `launcher_sfdc_campaigns.start_date`, ISO date string (Item E). */
    start_date: z.string().nullable(),
  })
  .strict();

export const CustomInviteLookupResponseSchema = z
  .object({
    /** Normalized (trimmed, lowercased) email the lookup ran against. */
    email: z.string(),
    /** null → no live salesforce_contact row → the send will be is_custom (D1). */
    contact: CustomInviteLookupContactSchema.nullable(),
    /** Every campaign this email has touched (SFDC membership OR a Terra invite), newest first, deduped by campaign. */
    campaigns: z.array(CustomInviteCampaignHistoryRowSchema),
  })
  .strict();

export const CustomInviteStatusSourceSchema = z.enum(['terra_invite', 'sfdc_campaign_member']);

export const CustomInviteStatusResponseSchema = z
  .object({
    email: z.string(),
    campaign_id: z.string(),
    already_invited: z.boolean(),
    /** Which table(s) had a hit — empty when already_invited is false. */
    sources: z.array(CustomInviteStatusSourceSchema),
    last_invited_at: z.string().nullable(),
  })
  .strict();

/** Mirrors the row shape `enroll()` returns — enroll-service.ts derives its type from this (U2). */
export const EnrollResultRowSchema = z.object({
  email: z.string(),
  status: z.enum(['ok', 'error']),
  executionId: z.string().optional(),
  correlationId: z.string().optional(),
  message: z.string().optional(),
  /** Stable machine-readable failure code (e.g. `'sender_unresolved'`, Item J) — see `sender-resolution.ts`'s `SENDER_UNRESOLVED_CODE`. `message` stays the human-readable text; the frontend keys UI decisions off this instead. */
  code: z.string().optional(),
});

export const EnrollResponseSchema = z.object({ results: z.array(EnrollResultRowSchema) });

/** GET /api/flow/enroll/options — sender options plus the live campaigns for the picker. */
export const EnrollOptionsResponseSchema = z.object({
  senders: z.array(SenderOptionSchema),
  campaigns: z.array(
    z.object({
      id: z.string(),
      name: z.string().nullable(),
      invite_subject: z.string().nullable(),
      invite_body: z.string().nullable(),
      start_date: z.string().nullable(),
    }),
  ),
});

export type CustomInviteLookupContact = z.infer<typeof CustomInviteLookupContactSchema>;
export type CustomInviteCampaignHistoryRow = z.infer<typeof CustomInviteCampaignHistoryRowSchema>;
export type CustomInviteLookupResponse = z.infer<typeof CustomInviteLookupResponseSchema>;
export type CustomInviteStatusSource = z.infer<typeof CustomInviteStatusSourceSchema>;
export type CustomInviteStatusResponse = z.infer<typeof CustomInviteStatusResponseSchema>;
export type EnrollResultRow = z.infer<typeof EnrollResultRowSchema>;
export type EnrollResponse = z.infer<typeof EnrollResponseSchema>;
export type EnrollOptionsResponse = z.infer<typeof EnrollOptionsResponseSchema>;
